import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class LesseeService {
    private propertyPromise = null;
    private leasePromise = null;
    private invoicesPromise = null;
    private propertiesConversationsPromiseMap = {};
    private accountSummaryPromise = null;

    constructor(private client: HttpClient) { }

    clearCache(): void {
        this.propertyPromise = null;
        this.leasePromise = null;
        this.invoicesPromise = null;
        this.propertiesConversationsPromiseMap = {};
        this.accountSummaryPromise = null;
    }

    getLeasedProperty() {
        if (this.propertyPromise == null) {
            this.propertyPromise = this.client.get(`${environment.apiBaseUrl}/v1/lessee/properties`).toPromise()
                .catch(err => {
                    this.propertyPromise = null;
                    throw err;
                });
        }
        return this.propertyPromise;
    }

    getActiveLease() {
        if (this.leasePromise == null) {
            this.leasePromise = this.client.get(`${environment.apiBaseUrl}/v1/lessee/leases`).toPromise()
                .catch(err => {
                    this.leasePromise = null;
                    throw err;
                });
        }
        return this.leasePromise;
    }

    getInvoices() {
        if(this.invoicesPromise == null) {
            this.invoicesPromise = this.client.get(`${environment.apiBaseUrl}/v1/lessee/invoices`).toPromise();
        }

        return this.invoicesPromise;
    }

    getInvoicePdf(invoiceId) {
        // Unique URL so a year-cached copy from an older API build is never reused.
        return this.client.get(`${environment.apiBaseUrl}/v1/lessee/properties/invoices/${invoiceId}/pdf`, {
            responseType: 'blob',
            params: new HttpParams().set('_', String(Date.now()))
        }).toPromise();
    }

    // Note: generateInvoicePdf and deleteInvoicePdf are not available for lessees
    // These methods are kept for backward compatibility but should not be called
    // The UI should hide these options for lessees

    getConversations(property, readStatus?, limit?) {
        let cacheKey = property.id + readStatus + limit;
        if(!this.propertiesConversationsPromiseMap[cacheKey]) {
          let params = new HttpParams();
    
          if(readStatus) {
            params = params.append("readStatus", readStatus);
          }
    
          if(limit) {
            params = params.append("limit", limit);
          }
    
          this.propertiesConversationsPromiseMap[cacheKey] = this.client.get(`${environment.apiBaseUrl}/v1/lessee/properties/conversations`, {params: params});
        }
    
        return this.propertiesConversationsPromiseMap[cacheKey];
    }
    
    clearConversations(property) {
        for(let cacheKey in this.propertiesConversationsPromiseMap) {
            if(cacheKey.indexOf(property.id) > -1) {
            this.propertiesConversationsPromiseMap[cacheKey] = null;
            } 
        }
    }

    respondToConversation(property, conversation, message, medium) {
        let response = {
            message: message
        };

        return this.client.post(`${environment.apiBaseUrl}/v1/lessee/properties/conversations/${conversation.id}/response?medium=${medium}`, response).toPromise().then((res) => {
            this.clearConversations(property);
        });
    }

    markConversationAsRead(property, conversation) {
        return this.client.post(`${environment.apiBaseUrl}/v1/lessee/properties/conversations/${conversation.id}/read`, {}).toPromise().then((res) => {
            this.clearConversations(property);
            return res;
        }).catch((err) => {
            // Silently handle 404 errors - route may not exist in older backend versions or backend needs restart
            if (err.status !== 404) {
                console.error('Error marking conversation as read:', err);
            }
            // Return resolved promise to prevent unhandled rejection
            return Promise.resolve();
        })
    }

    getAccountSummary() {
        if(this.accountSummaryPromise == null) {
            this.accountSummaryPromise = this.client.get(`${environment.apiBaseUrl}/v1/lessee/properties/invoices/summary`).toPromise();
        }

        return this.accountSummaryPromise;
    }

    createCardPayment(cardInfo) {
        return this.client.post(`${environment.apiBaseUrl}/v1/lessee/properties/invoices/payments`, cardInfo).toPromise().then((res) => {
            this.accountSummaryPromise = null;
            this.invoicesPromise = null;
        }).catch((err) => {
            throw err;
        })
    }
}
